import type { SupabaseClient } from "@supabase/supabase-js";
import posthog from "posthog-js";
import { insertCityWaitlistRow, waitlistEventProps, type WaitlistPlace } from "@/lib/cityWaitlistVote";
import { saveHomeLocation, type HomeLocation } from "@/lib/homeLocation";
import type { PickGrades } from "../../supabase/functions/_shared/picksRanking.ts";

/**
 * Answers given in onboarding before the account exists. They live on the
 * device next to grounded_pending_preferences, because Apple and Google sign
 * in inside the app and email confirmation all reload the app.
 */
export const PENDING_PICKS_KEY = "grounded_onboarding_pending_picks";
export const PENDING_LOCATION_KEY = "grounded_onboarding_pending_location";
export const PENDING_VOTE_KEY = "grounded_onboarding_pending_vote";
export const PENDING_TERMS_KEY = "grounded_onboarding_pending_terms";
/** Set once the onboarding location step is answered or skipped. */
export const ONBOARDING_LOCATION_ANSWERED_KEY = "grounded_onboarding_location_answered";

/** Same garbage collection bound as the pending preferences cache. */
export const PENDING_TTL_MS = 30 * 24 * 60 * 60 * 1000;
/**
 * The terms flag only covers a sign in that started on the Account screen,
 * so without an owning user id it expires quickly.
 */
export const TERMS_FLAG_TTL_MS = 2 * 60 * 60 * 1000;

export interface PendingPick {
  id: string;
  name: string;
  area: string | null;
  score: number | null;
  /** Kept with the pick so the Account card shows them after a reload or an Apple or Google round trip. */
  grades?: PickGrades | null;
  forYou?: boolean;
}

function read<T>(key: string): (T & { timestamp: number }) | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.timestamp !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

function write(key: string, value: object) {
  try {
    localStorage.setItem(key, JSON.stringify({ ...value, timestamp: Date.now() }));
  } catch { /* ignore */ }
}

function remove(key: string) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

const fresh = (ts: number, ttl = PENDING_TTL_MS) => Date.now() - ts < ttl;

// ── Picks ──
export function getPendingPicks(): PendingPick[] {
  const v = read<{ items: PendingPick[] }>(PENDING_PICKS_KEY);
  if (!v || !Array.isArray(v.items) || !fresh(v.timestamp)) return [];
  return v.items;
}

/** Toggle a heart. A café is held at most once. Returns true when now saved. */
export function togglePendingPick(pick: PendingPick): boolean {
  const items = getPendingPicks();
  const exists = items.some((p) => p.id === pick.id);
  const next = exists ? items.filter((p) => p.id !== pick.id) : [...items, pick];
  if (next.length === 0) remove(PENDING_PICKS_KEY);
  else write(PENDING_PICKS_KEY, { items: next });
  return !exists;
}

// ── Location ──
export function setPendingLocation(loc: HomeLocation) {
  write(PENDING_LOCATION_KEY, loc);
}
export function clearPendingLocation() {
  remove(PENDING_LOCATION_KEY);
}
export function getPendingLocation(): HomeLocation | null {
  const v = read<HomeLocation>(PENDING_LOCATION_KEY);
  if (!v || !fresh(v.timestamp) || !v.city) return null;
  return { city: v.city, state: v.state ?? null, lat: v.lat ?? null, lng: v.lng ?? null, source: v.source };
}
export function markLocationAnswered() {
  try {
    localStorage.setItem(ONBOARDING_LOCATION_ANSWERED_KEY, "true");
    // Also the key Browse and the location pop up already honor.
    localStorage.setItem("grounded_browse_location_prompted", "true");
  } catch { /* ignore */ }
}

// ── Vote ──
export function setPendingVote(place: WaitlistPlace) {
  write(PENDING_VOTE_KEY, place);
}
export function getPendingVote(): WaitlistPlace | null {
  const v = read<WaitlistPlace>(PENDING_VOTE_KEY);
  if (!v || !fresh(v.timestamp)) return null;
  if (typeof v.lat !== "number" || typeof v.lng !== "number") return null;
  return { searched_city: v.searched_city, city: v.city ?? null, state: v.state ?? null, lat: v.lat, lng: v.lng };
}

// ── Terms ──
/** Set when a sign up or sign in starts on the onboarding Account screen. */
export function setPendingTerms(userId?: string) {
  write(PENDING_TERMS_KEY, userId ? { userId } : {});
}
export function clearPendingTerms() {
  remove(PENDING_TERMS_KEY);
}
/** True when the flag applies to this user right now. */
export function pendingTermsAppliesTo(userId: string): boolean {
  const v = read<{ userId?: string }>(PENDING_TERMS_KEY);
  if (!v) return false;
  if (typeof v.userId === "string") return v.userId === userId && fresh(v.timestamp);
  return fresh(v.timestamp, TERMS_FLAG_TTL_MS);
}

export function clearAllPending() {
  [PENDING_PICKS_KEY, PENDING_LOCATION_KEY, PENDING_VOTE_KEY, PENDING_TERMS_KEY].forEach(remove);
}

function capture(event: string, props: Record<string, unknown>) {
  try { posthog.capture(event, props); } catch { /* ignore */ }
}

export interface ApplyResult {
  terms: boolean;
  picks: number;
  location: boolean;
  vote: boolean;
}

const inflight = new Map<string, Promise<ApplyResult>>();

/**
 * Writes everything held on the device to the account, right after it exists.
 * Each part clears only after its own write succeeds, so a failure retries on
 * the next sign in or app open. Safe to call more than once.
 */
export function applyOnboardingPending(
  client: SupabaseClient,
  user: { id: string; email?: string | null },
): Promise<ApplyResult> {
  const existing = inflight.get(user.id);
  if (existing) return existing;
  const run = (async (): Promise<ApplyResult> => {
    const result: ApplyResult = { terms: false, picks: 0, location: false, vote: false };

    // Terms: fill only when empty.
    if (pendingTermsAppliesTo(user.id)) {
      try {
        const { error } = await client
          .from("profiles")
          .update({ accepted_terms_at: new Date().toISOString() } as never)
          .eq("id", user.id)
          .is("accepted_terms_at", null);
        if (!error) { clearPendingTerms(); result.terms = true; }
      } catch { /* retry later */ }
    } else {
      // Expired or for another account: never record terms from it.
      clearPendingTerms();
    }

    // Saved cafés: one row per café, duplicates ignored by the unique key.
    const picks = getPendingPicks();
    if (picks.length > 0) {
      const ids = [...new Set(picks.map((p) => p.id))];
      try {
        const { error } = await client
          .from("user_favorites")
          .upsert(ids.map((cafe_id) => ({ user_id: user.id, cafe_id })) as never, {
            onConflict: "user_id,cafe_id",
            ignoreDuplicates: true,
          });
        if (!error) { remove(PENDING_PICKS_KEY); result.picks = ids.length; }
      } catch { /* retry later */ }
    }

    // Home area: never replaces one the account already has.
    const loc = getPendingLocation();
    if (loc) {
      try {
        const { data, error: readError } = await client
          .from("user_preferences")
          .select("home_city")
          .eq("user_id", user.id)
          .maybeSingle();
        if (!readError) {
          if ((data as { home_city?: string | null } | null)?.home_city) {
            clearPendingLocation();
          } else {
            // Never replace an existing home area; the one shared write.
            await saveHomeLocation(user.id, loc);
            clearPendingLocation();
            result.location = true;
          }
        }
      } catch { /* retry later */ }
    }

    // Vote: a second row carrying the account, like the waitlist card's email row.
    const vote = getPendingVote();
    if (vote) {
      const email = user.email ? user.email.toLowerCase() : null;
      try {
        // The one shared city_waitlist insert. With no email the device vote
        // index refuses this second row (23505), so the vote stays the guest
        // row; every sign up method gives an email, so this is documented only.
        const error = await insertCityWaitlistRow({ place: vote, email, userId: user.id, source: "onboarding" });
        if (!error || (error as { code?: string }).code === "23505") {
          remove(PENDING_VOTE_KEY);
          result.vote = true;
          if (email) {
            capture("city_waitlist_signed_up", {
              ...waitlistEventProps(vote, "onboarding"),
              is_signed_in: true,
              already_subscribed: (error as { code?: string } | null)?.code === "23505",
            });
          }
        }
      } catch { /* retry later */ }
    }

    return result;
  })().finally(() => inflight.delete(user.id));
  inflight.set(user.id, run);
  return run;
}
